import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, fromEvent, take } from 'rxjs';

import { ApiService } from '@app/services/api/api.service';
import {
  AutoQuoteOptionFeedbackEnum,
  PortalAutoQuoteOptionNode,
  PortalLeadNode,
} from '@app/services/api/api.types';
import { GoogleFlightOption } from '@app/services/google-flights/google-flights.types';
import { PortalAutoQuoteContextService } from '@app/services/portal-auto-quote/portal-auto-quote-context.service';
import { Tracking } from '@app/services/tracking/tracking.service';
import { PortalTrackingEvents } from '@app/utils/tracking-events';

@Component({
  selector: 'quick-option-itinerary',
  templateUrl: './quick-option-itinerary.component.html',
  styleUrl: './quick-option-itinerary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickOptionItineraryComponent implements OnInit {
  AutoQuoteOptionFeedbackEnum = AutoQuoteOptionFeedbackEnum;

  @Input() portalLead?: PortalLeadNode;
  @Input() passengerCountOverride?: number;
  @Input() flightOption: GoogleFlightOption;

  @Input() quoteToken?: string;
  @Input() autoQuoteOption?: PortalAutoQuoteOptionNode;
  @Input() isFirstQuoteFeedbackRow = false;

  apiService = inject(ApiService);
  portalAutoQuoteContextService = inject(PortalAutoQuoteContextService);
  destroyRef = inject(DestroyRef);

  submittingFeedback = signal(false);
  feedbackCoachmarkVisible = signal(false);

  ngOnInit() {
    Tracking.track(PortalTrackingEvents.quickQuoteShown, { ...this.flightOption });

    if (this.isFirstQuoteFeedbackRow && this.showQuoteFeedback() && !this.resolvedFeedback()) {
      this.feedbackCoachmarkVisible.set(true);

      fromEvent(document, 'click', { capture: true })
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.feedbackCoachmarkVisible.set(false));
    }
  }

  showQuoteFeedback(): boolean {
    return Boolean(this.quoteToken && this.autoQuoteOption);
  }

  resolvedFeedback(): AutoQuoteOptionFeedbackEnum | null {
    return this.autoQuoteOption?.clientFeedback ?? null;
  }

  submitOptionFeedback(feedback: AutoQuoteOptionFeedbackEnum) {
    const token = this.quoteToken;
    const optionName = this.autoQuoteOption?.optionName;

    if (!token || !optionName || this.submittingFeedback()) {
      return;
    }

    Tracking.track(
      feedback === AutoQuoteOptionFeedbackEnum.LIKED
        ? PortalTrackingEvents.portalAutoQuoteOptionLikePressed
        : PortalTrackingEvents.portalAutoQuoteOptionDislikePressed,
      { option_name: optionName },
    );

    this.submittingFeedback.set(true);

    this.apiService
      .setPortalAutoQuoteOptionFeedback({
        input: { token, optionLabel: optionName, feedback },
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.submittingFeedback.set(false)),
      )
      .subscribe({
        next: (res) => {
          if (res.ok && res.autoQuote) {
            this.portalAutoQuoteContextService.setAutoQuote(res.autoQuote);
          }
        },
      });
  }
}
